import { httpClient } from './core';

export type GdprRequestType = 'export' | 'delete';
export type GdprRequestStatus = 'pending' | 'processing' | 'completed' | 'failed';

export interface AdminGdprRequestDto {
  id: number;
  wpUserId: number;
  requestType: GdprRequestType;
  status: GdprRequestStatus;
  requestedVia: string;
  retentionMode: 'delete' | 'anonymize' | null;
  requestedAt: string;
  processedAt: string | null;
  errorMessage: string | null;
  result?: Record<string, unknown> | null;
  metadata?: Record<string, unknown> | null;
}

export interface AdminGdprRequestEventDto {
  id: number;
  gdprRequestId: number;
  eventType: string;
  eventData: Record<string, unknown> | null;
  createdAt: string;
}

export interface AdminGdprRequestDetailDto extends AdminGdprRequestDto {
  events: AdminGdprRequestEventDto[];
}

export interface GdprAutoProcessingSettingsDto {
  enabled: boolean;
  intervalHours: number;
  lastRunAt: string | null;
}

export const gdprAdminApi = {
  list: async (params?: {
    status?: GdprRequestStatus;
    type?: GdprRequestType;
    wpUserId?: number;
    limit?: number;
  }): Promise<AdminGdprRequestDto[]> => {
    const search = new URLSearchParams();
    if (params?.status) search.set('status', params.status);
    if (params?.type) search.set('type', params.type);
    if (params?.wpUserId && params.wpUserId > 0) search.set('wpUserId', String(params.wpUserId));
    if (params?.limit && params.limit > 0) search.set('limit', String(params.limit));
    const query = search.toString();
    const res = await httpClient<{ items: AdminGdprRequestDto[] }>(`/api/gdpr/requests${query ? `?${query}` : ''}`);
    if (res.error || !res.data) throw new Error(res.error || 'Fehler beim Laden der DSGVO-Anfragen');
    return res.data.items || [];
  },
  processOne: async (id: number, retentionMode?: 'anonymize'): Promise<{ ok: boolean; request: AdminGdprRequestDto }> => {
    const res = await httpClient<{ ok: boolean; request: AdminGdprRequestDto }>(`/api/gdpr/requests/${id}/process`, {
      method: 'POST',
      body: JSON.stringify(retentionMode ? { retentionMode, confirmed: true } : {}),
    } as RequestInit);
    if (res.error || !res.data) throw new Error(res.error || 'Fehler beim Verarbeiten der DSGVO-Anfrage');
    return res.data;
  },
  getOne: async (id: number): Promise<AdminGdprRequestDetailDto> => {
    const res = await httpClient<{ item: AdminGdprRequestDetailDto }>(`/api/gdpr/requests/${id}`);
    if (res.error || !res.data) throw new Error(res.error || 'Fehler beim Laden der DSGVO-Detailansicht');
    return res.data.item;
  },
  runJob: async (limit = 100): Promise<{ ok: boolean; result: { pendingFound: number; completed: number; failed: number; requiresManualReview: number } }> => {
    const res = await httpClient<{ ok: boolean; result: { pendingFound: number; completed: number; failed: number; requiresManualReview: number } }>('/api/jobs/gdpr/run', {
      method: 'POST',
      body: JSON.stringify({ limit }),
    } as RequestInit);
    if (res.error || !res.data) throw new Error(res.error || 'Fehler beim Starten des DSGVO-Jobs');
    return res.data;
  },
  getAutoSettings: async (): Promise<GdprAutoProcessingSettingsDto> => {
    const res = await httpClient<GdprAutoProcessingSettingsDto>('/api/gdpr/settings/auto-processing');
    if (res.error || !res.data) throw new Error(res.error || 'Fehler beim Laden der DSGVO-Auto-Einstellungen');
    return res.data;
  },
  updateAutoSettings: async (payload: Partial<GdprAutoProcessingSettingsDto>): Promise<GdprAutoProcessingSettingsDto> => {
    const res = await httpClient<GdprAutoProcessingSettingsDto>('/api/gdpr/settings/auto-processing', {
      method: 'PUT',
      body: JSON.stringify(payload),
    } as RequestInit);
    if (res.error || !res.data) throw new Error(res.error || 'Fehler beim Speichern der DSGVO-Auto-Einstellungen');
    return res.data;
  },
};
